import { useState, useCallback, useRef } from 'react';
import { searchByLottoPubblico, downloadDocument } from '../services/sharePointApi';

/**
 * Sort documents alphabetically by name
 * @param {Array} docs - Array of document objects
 * @returns {Array} - Sorted array
 */
const sortDocuments = (docs) => {
  return [...docs].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};

/**
 * Custom hook for document search functionality
 * @returns {Object} - Hook state and functions
 */
export const useDocumentSearch = () => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [productInfo, setProductInfo] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Still loading more pages
  const searchIdRef = useRef(0); // To prevent stale updates

  /**
   * Search for documents by lotto pubblico (serial number)
    * 1. Searches configured Dataverse entity set by lotto pubblico
    * 2. Reads SchemaE values from Dataverse result
    * 3. For each non-empty SchemaE value, searches SharePoint documents by Nome
   * @param {string} lottoPubblico - The lotto pubblico to search for
   */
  const search = useCallback(async (lottoPubblico) => {
    if (!lottoPubblico || lottoPubblico.trim() === '') {
      setError('Please enter a lotto pubblico');
      return;
    }

    setIsLoading(true);
    setIsLoadingMore(true);
    setError(null);
    setSearchPerformed(true);
    setDocuments([]);
    setProductInfo(null);
    
    // Increment search ID to invalidate any in-flight updates from previous searches
    const currentSearchId = ++searchIdRef.current;

    try {
      // Use the new complete search flow with progress callback
      const result = await searchByLottoPubblico(lottoPubblico.trim(), (partialResult) => {
        // Only update if this is still the current search
        if (searchIdRef.current === currentSearchId) {
          setProductInfo(partialResult.productInfo);
          setDocuments(sortDocuments(partialResult.documents));
          // After first results arrive, show them but keep loading indicator
          setIsLoading(false);
        }
      });
      
      // Final update with complete results (only if still current search)
      if (searchIdRef.current === currentSearchId) {
        setProductInfo(result.productInfo);
        setDocuments(sortDocuments(result.documents));
      }
    } catch (err) {
      if (searchIdRef.current === currentSearchId) {
        setError(err.message || 'An error occurred while searching for documents');
        setDocuments([]);
        setProductInfo(null);
      }
    } finally {
      if (searchIdRef.current === currentSearchId) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, []);

  /**
   * Download a specific document
   * @param {Object} document - The document object with name
   */
  const download = useCallback(async (document) => {
    try {
      // downloadDocument already returns a valid Blob from response.blob()
      const blob = await downloadDocument(document);
      
      // Use the blob directly without re-wrapping it
      // The blob already has the correct content-type from the API response
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.name || 'document';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (err) {
      setError(err.message || 'An error occurred while downloading the document');
      return false;
    }
  }, []);

  /**
   * Clear search results and reset state
   */
  const clearResults = useCallback(() => {
    setDocuments([]);
    setError(null);
    setSearchPerformed(false);
    setProductInfo(null);
    setIsLoadingMore(false);
  }, []);

  return {
    documents,
    isLoading,
    isLoadingMore, // True when still loading more pages (shows "loading more..." indicator)
    error,
    searchPerformed,
    productInfo,
    search,
    download,
    clearResults,
  };
};

export default useDocumentSearch;
